import React from 'react';
import { SlideWrapper, GlassCard, SectionLabel } from './SharedComponents';

export const ImpactEquationDetailedSlide = ({ slide }) => {
  const termColors = ['#22c55e', '#8b5cf6', '#0068ff', '#06b6d4'];
  const termIcons = [
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
  ];

  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#060614] to-[#030308]" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-[#0068ff] opacity-[0.07] blur-[200px] rounded-full" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#22c55e] opacity-[0.03] blur-[120px] rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#8b5cf6] opacity-[0.03] blur-[120px] rounded-full" />
        <div className="absolute inset-0 opacity-[0.012]" style={{
          backgroundImage: 'linear-gradient(rgba(0,104,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(0,104,255,0.2) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center h-full px-6 py-4 overflow-hidden">
        <SectionLabel delay={0}>The Formula</SectionLabel>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mt-3 mb-1 animate-slide-up">{slide.title}</h2>
        <p className="text-white/35 text-sm mb-5 animate-fade-in" style={{ animationDelay: '0.05s' }}>{slide.subtitle}</p>

        <GlassCard delay={0.1} className="px-8 py-3.5 mb-5" style={{ borderColor: '#0068ff30', boxShadow: '0 0 50px rgba(0,104,255,0.08)' }}>
          <div className="flex items-center gap-2 text-lg md:text-xl lg:text-2xl font-bold">
            <span className="text-[#0068ff]">Impact</span>
            <span className="text-white/25">=</span>
            <span className="text-white/20">(</span>
            <span className="text-[#22c55e]">Action</span>
            <span className="text-white/25">x</span>
            <span className="text-[#8b5cf6]">Quality</span>
            <span className="text-white/20">)</span>
            <span className="text-white/25">x</span>
            <span className="text-[#0068ff]">Verification</span>
            <span className="text-white/25">x</span>
            <span className="text-[#06b6d4]">Network</span>
          </div>
        </GlassCard>

        <div className="flex items-stretch gap-3 w-full max-w-[1050px] mb-4">
          {slide.terms.map((term, idx) => (
            <React.Fragment key={idx}>
              <GlassCard delay={0.2 + idx * 0.08} className="flex-1 p-4" style={{
                borderColor: `${termColors[idx]}25`, background: `linear-gradient(180deg, ${termColors[idx]}08, transparent)`
              }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${termColors[idx]}15`, color: termColors[idx] }}>{termIcons[idx]}</div>
                  <span className="text-sm font-bold" style={{ color: termColors[idx] }}>{term.name}</span>
                </div>
                <p className="text-white/40 text-xs leading-relaxed mb-1.5">{term.description}</p>
                <p className="text-white/25 text-[10px] italic">{term.examples}</p>
              </GlassCard>
              {idx < slide.terms.length - 1 && (
                <div className="flex items-center shrink-0 self-center">
                  <span className="text-white/15 text-lg font-bold">x</span>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <GlassCard delay={0.55} className="w-full max-w-[1050px] px-5 py-3.5 mb-4" style={{ borderColor: 'rgba(0,104,255,0.15)' }}>
          <div className="text-[10px] uppercase tracking-widest text-white/25 mb-2">Example</div>
          <div className="flex items-center gap-3">
            {slide.example.steps.map((step, idx) => (
              <React.Fragment key={idx}>
                <div className="flex-1 flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold mt-0.5" style={{ background: `${termColors[idx]}20`, color: termColors[idx] }}>{idx + 1}</div>
                  <div>
                    <span className="text-white/60 text-xs">{step.text}</span>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ color: termColors[idx], background: `${termColors[idx]}15` }}>{step.label}</span>
                      <span className="text-white/20 text-[9px]">{step.result}</span>
                    </div>
                  </div>
                </div>
                {idx < slide.example.steps.length - 1 && (
                  <svg viewBox="0 0 20 10" className="w-4 h-2 shrink-0"><path d="M 2 5 L 16 5" fill="none" stroke="white" strokeWidth="1" strokeOpacity="0.15" /><path d="M 13 2 L 18 5 L 13 8" fill="none" stroke="white" strokeWidth="1" strokeOpacity="0.15" /></svg>
                )}
              </React.Fragment>
            ))}
          </div>
        </GlassCard>

        <div className="flex items-center gap-2.5 mb-3 animate-slide-up" style={{ animationDelay: '0.65s' }}>
          {['Action', 'Quality', 'Verification', 'Network', 'Impact'].map((label, idx) => {
            const colors = ['#22c55e', '#8b5cf6', '#0068ff', '#06b6d4', '#0068ff'];
            const isLast = idx === 4;
            return (
              <React.Fragment key={idx}>
                <div className="px-3 py-1.5 rounded-lg text-center" style={{
                  background: `${colors[idx]}${isLast ? '15' : '08'}`,
                  border: `1px solid ${colors[idx]}${isLast ? '40' : '20'}`,
                  boxShadow: isLast ? `0 0 20px ${colors[idx]}15` : 'none'
                }}>
                  <span className={`text-[11px] font-bold ${isLast ? '' : ''}`} style={{ color: colors[idx] }}>{label}</span>
                </div>
                {idx < 4 && (
                  <svg viewBox="0 0 20 10" className="w-4 h-2"><defs><marker id={`eqA${idx}`} markerWidth="5" markerHeight="4" refX="4" refY="2" orient="auto"><polygon points="0 0,5 2,0 4" fill={colors[idx + 1]} opacity="0.5"/></marker></defs><path d="M 2 5 L 15 5" fill="none" stroke={colors[idx + 1]} strokeWidth="1" strokeOpacity="0.3" markerEnd={`url(#eqA${idx})`} /></svg>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <GlassCard delay={0.75} className="px-6 py-2.5 text-center max-w-2xl" style={{ borderColor: 'rgba(0,104,255,0.2)' }}>
          <span className="text-white/45 text-xs">{slide.bottomLine[0]} {slide.bottomLine[1]}</span>
          <br />
          <span className="text-[#0068ff] text-xs font-semibold">{slide.bottomLine[2]}</span>
        </GlassCard>
      </div>
    </SlideWrapper>
  );
};
